<template>
  <section class="hero is-medium is-primary is-bold">
    <div class="hero-head"></div>
    <div class="hero-body">
      <div class="container">
        <h1 class="title">
          あなたの生存力スコア
        </h1>
        <h2 class="subtitle" style="font-size: 0.6rem;"></h2>
      </div>

      <div class="container result-circle">
        <progress-bar type="circle" ref="circle" :options="options"></progress-bar>
      </div>

      <transition name="fade">
        <div v-show="scoreShown">

          <div class="chart-container">
            <canvas id="canvas" ref="distribution"></canvas>
          </div>

          <p class="description" style="text-align: center;">{{ description }}</p>

          <p style="margin: 2rem auto; text-align: center;">
            <a class="button is-text has-text-white" @click="$router.push('scoring')">もう一度スコアリング</a>
          </p>

          <p class="buttons">
            <a class="button share is-primary is-medium"
               style="background: #55acee;"
               target="_blank"
               :href="twitterShareURL">
              <span class="icon is-small">
                <i class="fab fa-twitter"></i>
              </span>
            </a>
            <a class="button share is-primary is-medium"
               style="background: #3B5998;"
               target="_blank"
               :href="facebookShareURL">
              <span class="icon is-small">
                <i class="fab fa-facebook"></i>
              </span>
            </a>
            <a class="button share is-primary is-medium"
               style="background: #1dcd00;"
               target="_blank"
               :href="lineSendURL">
              <span class="icon is-small">
                <i class="fab fa-line"></i>
              </span>
            </a>
          </p>
          <footer style="text-align: center; margin-top: 2rem;">
            <p style="font-size: 0.6rem;">Powered by</p>
            <a href="https://www.sxi.co.jp/" style="cursor: pointer;">
              <img src="../../static/logo_footer.png" alt="Logo" width="50%" height="50%">
            </a>
          </footer>
        </div>

      </transition>

    </div>

    <div class="hero-foot"></div>
  </section>
</template>

<script>
import BField from 'buefy/src/components/field/Field'
import BTableColumn from 'buefy/src/components/table/TableColumn'

import Chart from 'chart.js'

export default {
  name: 'Result',
  components: {
    BTableColumn,
    BField
  },
  data () {
    return {
      score: 0,
      scoreShown: false,
      description: '',
      chart: null,
      options: {
        // Stroke color.
        // Default: '#555'
        color: '#3a3a3a',

        // Width of the stroke.
        // Unit is percentage of SVG canvas' size.
        // Default: 1.0
        // NOTE: In Line shape, you should control
        // the stroke width by setting container's height.
        // WARNING: IE doesn't support values over 6, see this bug:
        //          https://github.com/kimmobrunfeldt/progressbar.js/issues/79
        strokeWidth: 8.1,

        // If trail options are not defined, trail won't be drawn

        // Color for lighter trail stroke
        // underneath the actual progress path.
        // Default: '#eee'
        trailColor: '#f4f4f4',

        // Width of the trail stroke. Trail is always centered relative to
        // actual progress path.
        // Default: same as strokeWidth
        trailWidth: 8,

        // Inline CSS styles for the created SVG element
        // Set null to disable all default styles.
        // You can disable individual defaults by setting them to `null`
        // If you specify anything in this object, none of the default styles
        // apply
        svgStyle: {
          display: 'block',

          // Important: make sure that your container has same
          // aspect ratio as the SVG canvas. See SVG canvas sizes above.
          width: '60%'
        },

        // Text options. Text element is a <p> element appended to container
        // You can add CSS rules for the text element with the className
        // NOTE: When text is set, 'position: relative' will be set to the
        // container for centering. You can also prevent all default inline
        // styles with 'text.style: null'
        // Default: null
        text: {
          // Initial value for text.
          // Default: null
          value: null,

          // Class name for text element.
          // Default: 'progressbar-text'
          className: 'progressbar__label',

          // Inline CSS styles for the text element.
          // If you want to modify all CSS your self, set null to disable
          // all default styles.
          // If the style option contains values, container is automatically
          // set to position: relative. You can disable behavior this with
          // autoStyleContainer: false
          // If you specify anything in this object, none of the default styles
          // apply
          // Default: object speficied below
          style: {
            // Text color.
            // Default: same as stroke color (options.color)
            color: '#ffffff',
            position: 'absolute',
            left: '50%',
            top: '50%',
            padding: 0,
            margin: 0,
            // You can specify styles which will be browser prefixed
            transform: {
              prefix: true,
              value: 'translate(-50%, -50%)'
            }
          },

          // Only effective if the text.style is not null
          // By default position: relative is applied to container if text
          // is set. Setting this to false disables that feature.
          autoStyleContainer: true,

          // Only effective if the shape is SemiCircle.
          // If true, baseline for text is aligned with bottom of
          // the SVG canvas. If false, bottom line of SVG canvas
          // is in the center of text.
          // Default: true
          alignToBottom: true
        },

        // Fill color for the shape. If null, no fill.
        // Default: null
        // fill: 'rgba(0, 0, 0, 0.5)',

        // Duration for animation in milliseconds
        // Default: 800
        duration: 2000,

        // Easing for animation. See #easing section.
        // Default: 'linear'
        easing: 'easeOut',

        // See #custom-animations section
        // Built-in shape passes reference to itself and a custom attachment
        // object to step function
        from: {color: '#eee'},
        to: {color: '#167df0'},
        step: function (state, circle, attachment) {
          circle.path.setAttribute('stroke', state.color)
        },

        // If true, some useful console.warn calls will be done if it seems
        // that progressbar is used incorrectly
        // Default: false
        warnings: false
      }
    }
  },
  computed: {
    twitterShareURL () {
      const originURL = encodeURIComponent(location.href)
      const text = encodeURIComponent('私の生存力スコアは' + this.score + '点でした。あなたも算出してみよう。')
      return 'https://twitter.com/intent/tweet?text=' + text + '&hashtags=SXスコア,生存力スコア' + '&url=' + originURL
    },
    facebookShareURL () {
      return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(location.href)
    },
    lineSendURL () {
      return 'https://social-plugins.line.me/lineit/share?url=' + encodeURIComponent(location.href)
    }
  },
  async mounted () {
    const score = this.$route.query.score
    this.score = score

    const evaluation = this.evaluate(score)
    this.description = evaluation[1]

    const scorePer = score / 1000

    for (let i = 0; i < score; i++) {
      await this.drumRollScore(i, this)
    }

    this.$refs.circle.animate(scorePer)
    this.$refs.circle.setText(score)

    this.scoreShown = true

    setTimeout(() => { this.renderChart(score) }, 1.5)

  },
  methods: {
    reset () {
      let initialData = this.$options.data()
      this.form = initialData.form
      this.upload = initialData.upload
      this.dataInput = initialData.dataInput
    },
    drumRollScore (count, $parent) {
      return new Promise(resolve => {
        $parent.$refs.circle.setText(count)
        setTimeout(() => {
          resolve()
        }, 1)
      })
    },
    evaluate (score) {
      if (score <= 300) {
        return [0, '自分を見つめ直す時です。']
      } else if (score <= 400) {
        return [1, 'これから頑張って下さい。']
      } else if (score <= 500) {
        return [3, '普通です。']
      } else if (score <= 600) {
        return [4, 'まだまだ普通です。']
      } else if (score <= 650) {
        return [5, 'それなりに楽しく生き抜くことができるでしょう。']
      } else {
        return [6, '真のサバイバーです。どんな苦境でも楽しく生き抜くことが出来るでしょう。']
      }
    },
    renderChart (score) {
      const colorSet = this.distributeColor(Number(score))
      let dataSet = this.getDataSet(colorSet)

      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }

      const canvas = document.getElementById('canvas')

      canvas.height = 220

      this.chart = new Chart(canvas, dataSet)
      this.chart.generateLegend()
    },
    getDataSet (colorSet) {

      const dataCollection = {
        labels: ['0~', '', '', '', '', '500', '', '', '', '~1000'],
        datasets: [{
          label: 'あなたのスコアの分布',
          data: [1, 2, 3, 7, 18, 33, 20, 9, 5, 3],
          backgroundColor: colorSet,
          borderColor: colorSet,
          borderWidth: 1
        }]
      }

      const options = {
        events: [],
        maintainAspectRatio: true,
        legend: {
          display: true,
          onClick () {
          },
          labels: {
            fontColor: 'white',
            generateLabels (chart) {
              return chart.data.datasets.map(function (dataset, i) {
                return {
                  text: dataset.label,
                  fillStyle: 'rgba(22, 125, 240, 1)',
                  hidden: !chart.isDatasetVisible(i),
                  lineCap: dataset.borderCapStyle,
                  lineDash: [],
                  lineDashOffset: 0,
                  lineJoin: dataset.borderJoinStyle,
                  lineWidth: dataset.pointBorderWidth,
                  strokeStyle: 'rgba(22, 125, 240, 1)',
                  pointStyle: dataset.pointStyle,
                  datasetIndex: i
                }
              })

            }
          }
        },
        scales: {
          yAxes: [{
            gridLines: {
              display: false,
              color: 'rgba(255, 255, 255, 0.5)'
            },
            scaleLabel: {
              display: false,
              labelString: '',
              fontColor: 'white',
              fontSize: 12
            },
            ticks: {
              fontColor: 'white',
              beginAtZero: true,
              callback: function (value, index, values) {
                return value + '%'
              }

            }
          }],
          xAxes: [{
            gridLines: {
              display: false,
              color: 'rgba(255, 255, 255, 0.5)'
            },
            scaleLabel: {
              display: true,
              labelString: 'スコア',
              fontColor: 'white',
              fontSize: 12
            },
            ticks: {
              fontColor: 'white',
              max: 0,
              min: 1000,
              showLine: false,
              maxRotation: 0
            }
          }]
        }
      }

      return {
        type: 'bar',
        data: dataCollection,
        options: options
      }
    },
    distributeColor (score) {
      const scoreBandList = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]
      let colorSet = []

      for (let i = 0; i < scoreBandList.length; i++) {
        let color = 'rgba(225, 225, 225, 0.6)'
        if (i === 0) {
          if (score <= 100) {
            color = 'rgba(22, 125, 240, 1)'
          }
        } else {
          if (scoreBandList[i] - 100 <= score && score < scoreBandList[i]) {
            color = 'rgba(22, 125, 240, 1)'
          }
        }
        colorSet.push(color)
      }
      return colorSet
    }
  }
}
</script>

<style scoped>

  .hero {
    background: white;
    height: 100%;
  }

  .subtitle {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom-style: solid;
    border-bottom-width: 1px;
    border-bottom-color: #8F99A3;
  }

  a.button.share {
    margin: 1rem;
  }

  .result-circle {
    margin-top: 2rem;
    width: 80%;
    text-align: center;
  }

  .buttons {
    display: block;
    text-align: center;
  }

  .description {
    margin-top: 2rem;
  }

  .chart-container {
    width: 90%;
    margin: 3rem auto 0 auto;
    text-align: center;
    max-width: 380px;
  }

</style>

<style>
  .label {
    color: white;
  }

  .progressbar__label {
    font-size: 2rem;
    color: white;
  }

  svg {
    max-width: 500px;
    max-height: 500px;
    margin: auto;
  }

  .fade-enter{
      opacity: 0;
  }
  .fade-enter-active{
      transition: opacity 1.5s;
  }
  .fade-leave{
      /* opacity: 1; */
  }
  .fade-leave-active{
      transition: opacity 1.5s;
      opacity: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/Result.vue